import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import classnames from "classnames";
import { Button, IconS3 } from "@aqar-app/aqar-design-system";
import styles from "./modal.module.scss";

const noop = () => {};

const Modal = ({
  title,
  isError,
  children,
  visible,
  footer,
  okLabel,
  okLink,
  cancelLabel,
  closeBtn,
  onOk,
  onCancel,
  okButtonProps,
  cancelButtonProps,
  closeOnClickOutside,
  headerIcon,
  // eslint-disable-next-line react/prop-types
  btnsList,
  wrapperClassName,
  modalClassName,
  contentClassName,
  bodyClassName,
  ...restProps
}) => {
  const [isVisible, setIsVisible] = useState(visible);

  const modalRef = useRef(null);

  const handleClickOutSide = (e) => {
    if (!modalRef?.current?.contains(e.target)) {
      onCancel();
      setIsVisible(false);
    }
  };

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (closeOnClickOutside) {
      window.addEventListener("click", handleClickOutSide);
      return () => window.removeEventListener("click", handleClickOutSide);
    }
  }, [visible]);

  useEffect(() => {
    if (visible !== isVisible) setIsVisible(visible);
  }, [visible]);

  useEffect(() => {
    if (isVisible) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
  }, [isVisible]);

  const wrapperClassname = classnames(styles.modalWrapper, wrapperClassName);
  const modalClassname = classnames(styles.modal, modalClassName);
  const contentClassname = classnames(styles.content, contentClassName);
  const bodyClassname = classnames(styles.body, bodyClassName);

  const modal = (
    <>
      <div className={styles.modalMask} />
      <div className={wrapperClassname}>
        <div className={modalClassname} ref={modalRef} {...restProps}>
          <div className={contentClassname}>
            {!!title && <div className={styles.header}>{title}</div>}
            {!!closeBtn && (
              <IconS3
                className={styles.closeIcon}
                type="Close"
                onClick={onCancel}
              />
            )}
            {!!children && (
              <div className={bodyClassname}>
                {!!headerIcon && (
                  <IconS3 className={styles.headerIcon} type={headerIcon} />
                )}
                {children}
              </div>
            )}
            {footer && (
              <div
                className={styles.footer}
                style={{ textAlign: closeBtn ? "center" : "right" }}
              >
                {okLabel &&
                  (okLink ? (
                    <a href={okLink} target="_self" className={styles.okLink}>
                      {okLabel}
                    </a>
                  ) : (
                    <Button
                      label={okLabel}
                      priority={isError ? "secondary" : "primary"}
                      onClick={onOk}
                      {...okButtonProps}
                    />
                  ))}
                {cancelLabel && (
                  <Button
                    label={cancelLabel}
                    priority="secondary"
                    onClick={onCancel}
                    {...cancelButtonProps}
                  />
                )}
                {/* eslint-disable-next-line react/prop-types */}
                {btnsList?.map((btn) => (
                  <Button
                    key={btn.label}
                    label={btn.label}
                    priority={btn.isError ? "secondary" : "primary"}
                    onClick={btn.action}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return isVisible
    ? ReactDOM.createPortal(modal, document.getElementById("modal-container"))
    : null;
};

Modal.propTypes = {
  title: PropTypes.string,
  headerIcon: PropTypes.string,
  children: PropTypes.node,
  visible: PropTypes.bool,
  footer: PropTypes.bool,
  closeBtn: PropTypes.bool,
  isError: PropTypes.bool,
  okLabel: PropTypes.string,
  okLink: PropTypes.string,
  cancelLabel: PropTypes.string,
  onOk: PropTypes.func,
  onCancel: PropTypes.func,
  okButtonProps: PropTypes.shape({}),
  cancelButtonProps: PropTypes.shape({}),
  closeOnClickOutside: PropTypes.bool,
  wrapperClassName: PropTypes.string,
  modalClassName: PropTypes.string,
  contentClassName: PropTypes.string,
  bodyClassName: PropTypes.string,
};

Modal.defaultProps = {
  title: "",
  headerIcon: "",
  children: undefined,
  visible: false,
  closeBtn: false,
  isError: false,
  footer: true,
  okLabel: "",
  okLink: "",
  cancelLabel: "",
  onOk: noop,
  onCancel: noop,
  okButtonProps: {},
  cancelButtonProps: {},
  closeOnClickOutside: true,
  wrapperClassName: "",
  modalClassName: "",
  contentClassName: "",
  bodyClassName: "",
};

export default Modal;
